import {
	borderRadius,
	customSizes,
	fontFeatureSettings,
	palette,
	spaces,
	typography,
} from '@kimuno/theme';
import { ListItem, ListItemButton, ListItemIcon, ListItemText, SvgIcon } from '@mui/material';
import { Link } from 'found';
import kebabCase from 'lodash/kebabCase';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { clearSearchId } from '../../../utils/ampli';
import getMuiIcon, { type IconName } from '../../../utils/getMuiIcon';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import MenuItemTooltip from './MenuItemTooltip';

interface MenuItemProps {
	icon: IconName;
	label: string;
	to?: string;
	external?: boolean;
	isCollapsed: boolean;
	onClick?: (() => void) | undefined;
	isFocused?: boolean;
	activePaths?: string[];
}

// Constants
const ICON_SIZE = '16px';

const listItemIconStyles = {
	minWidth: ICON_SIZE,
	width: ICON_SIZE,
	color: 'inherit',
	padding: 0,
};

const iconStyles = {
	width: ICON_SIZE,
	height: ICON_SIZE,
};

const listItemTextStyles = {
	margin: '0px',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: spaces.sm,
	'& .MuiListItemText-primary': {
		fontFeatureSettings: fontFeatureSettings.disableLigatures,
	},
};

export const createStateStyles = (backgroundColor: string, textColor: string) => ({
	backgroundColor,
	color: textColor,
	'& .MuiListItemText-primary': {
		color: textColor,
	},
	'& .MuiSvgIcon-root': {
		color: textColor,
	},
});

export const transitionConfig = {
	duration: '100ms',
	easing: 'ease-in-out',
};

const hoverStyles = createStateStyles(palette.action.hover, palette.text.primary);
const focusStyles = createStateStyles(palette.action.focus, palette.text.primary);
const selectedStyles = createStateStyles(palette.primary.selected, palette.primary.main);

const getListItemButtonStyles = (
	isActive: boolean,
	isFocused: boolean,
	isCollapsed: boolean,
	compactCollapsed: boolean
) => ({
	paddingX: isCollapsed ? (compactCollapsed ? spaces.xxs : spaces.xs) : spaces.sm,
	paddingY: isCollapsed ? (compactCollapsed ? spaces.xxs : spaces.xxs) : 0,
	height: isCollapsed
		? compactCollapsed
			? '28px'
			: customSizes.sideNavbar.itemHeight
		: customSizes.sideNavbar.itemHeight,
	minHeight: isCollapsed
		? compactCollapsed
			? '28px'
			: customSizes.sideNavbar.itemHeight
		: customSizes.sideNavbar.itemHeight,
	width: isCollapsed && compactCollapsed ? 'fit-content' : undefined,
	borderRadius: isCollapsed
		? compactCollapsed
			? borderRadius.xxs
			: borderRadius.xs
		: borderRadius.sm,
	gap: spaces.xs,
	alignItems: 'center',
	justifyContent: isCollapsed && compactCollapsed ? 'center' : undefined,
	color: palette.text.primary,
	transition: `background-color ${transitionConfig.duration} ${transitionConfig.easing}, 
    color ${transitionConfig.duration} ${transitionConfig.easing}`,
	...(isFocused && focusStyles),
	...(isActive && selectedStyles),
	'&:hover': hoverStyles,
	'& .MuiListItemText-primary': {
		transition: `color ${transitionConfig.duration} ${transitionConfig.easing}`,
	},
	'& .MuiSvgIcon-root': {
		transition: `color ${transitionConfig.duration} ${transitionConfig.easing}`,
	},
});

const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>(
	(
		{ icon, label, to, external = false, isCollapsed, onClick, isFocused = false, activePaths },
		ref
	) => {
		const { t } = useTranslation();
		const { match, router } = useRouter();
		const { isLgScreen } = useScreenSize();
		const compactCollapsed = isCollapsed && !isLgScreen;

		const isActive = useMemo(() => {
			if (external || !to) return false;
			const currentPath = match.location.pathname.replace(/^\//, '');
			if (activePaths) {
				return activePaths.some((activePath) => currentPath.startsWith(activePath));
			}
			const targetPath = to.split('?')[0].replace(/^\//, '');
			return currentPath.startsWith(targetPath);
		}, [external, to, match.location.pathname, activePaths]);

		const linkProps = useMemo(
			() =>
				external
					? {
							component: 'a',
							href: to,
							target: '_blank',
							rel: 'noopener noreferrer',
						}
					: {
							component: Link,
							to: `/${to}`,
						},
			[external, to]
		);

		const handleClick = (e: React.MouseEvent) => {
			if (to && !to.startsWith('search')) clearSearchId();
			if (isActive && !external && to && onClick) {
				e.preventDefault();
				onClick();
				router.push(`/${to}`);
			} else if (onClick) {
				onClick();
			}
		};

		const translatedLabel = t(label);
		const listItemSx = compactCollapsed ? { display: 'flex', justifyContent: 'center' } : undefined;
		const content = (
			<ListItem
				ref={ref}
				dense
				disableGutters={false}
				disablePadding
				sx={listItemSx}
				{...linkProps}
				onClick={handleClick}
			>
				<ListItemButton
					sx={getListItemButtonStyles(isActive, isFocused, isCollapsed, compactCollapsed)}
					data-testid={`menu-item-${kebabCase(label)}`}
					data-active={isActive}
					data-focused={isFocused}
					aria-label={isCollapsed ? translatedLabel : undefined}
				>
					<ListItemIcon sx={listItemIconStyles} aria-hidden="true">
						<SvgIcon component={getMuiIcon(icon)} sx={iconStyles} aria-hidden="true" />
					</ListItemIcon>
					{!isCollapsed && (
						<ListItemText
							primary={translatedLabel}
							sx={listItemTextStyles}
							primaryTypographyProps={{
								...typography.menu.itemDense,
								color: 'inherit',
							}}
						/>
					)}
				</ListItemButton>
			</ListItem>
		);

		return isCollapsed ? (
			<MenuItemTooltip title={t(label)}>
				<span>{content}</span>
			</MenuItemTooltip>
		) : (
			content
		);
	}
);

MenuItem.displayName = 'MenuItem';

export default MenuItem;
